import React, {useState, useRef} from "react";
import {useNavigate} from 'react-router-dom'; 
import {LucideRocket, LucideShoppingCart, X} from 'lucide-react';
import { BaseModal } from "./ModalBase";
import { Typography } from "../ui/Typography";
import { Button } from "../ui/Button";
import {CheckoutModal} from "./CheckoutModal";

export interface CreditPackageData {
    name : string;
    totalCredits : number;
    totalPrice : number;
    isPopular? : boolean;
}

interface CreditPricingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPurchase: (pkg: CreditPackageData) => void;
}

export const PACKAGE_DATA: CreditPackageData[] = [
    {
        name: "5 CREDITS",
        totalCredits: 5,
        totalPrice: 1.99,
    },
    {
        name: "20 CREDITS",
        totalCredits: 20,
        totalPrice: 9.99,
        isPopular: true,
    },
    {
        name: "50 CREDITS",
        totalCredits: 50,
        totalPrice: 19.99,
    },
];


export const CreditPricingModal = ({isOpen, onClose, onPurchase}: CreditPricingModalProps) => {
    const [selectedPackage, setSelectedPackage] = useState<CreditPackageData>(PACKAGE_DATA[0]);

    const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
    function toggleOrderSummary() {
        setIsOrderSummaryOpen(prev => !prev);
    }
    
    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={onClose}
                containerClassName="w-full max-w-3xl rounded-2xl p-8 md:p-10"
            >
                <Button
                    intent="icon"
                    onClick={onClose}
                    className="absolute top-2 right-2 hover:text-surface-100"
                >
                    <X size={24}/>
                </Button>
    
                <div className="text-center mb-8">
                    <Typography as="h2" variant="h2" className="sm:text-4xl">
                        Top Up Your Credits
                    </Typography>
                    <Typography as="p">
                        Credits never expire and can be used on any icon or pack.
                    </Typography>
                </div>
    
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {PACKAGE_DATA.map(pack => (
                        <CreditPackske
                            key={pack.name}
                            data={pack}
                            isSelected={selectedPackage.name === pack.name}
                            onSelect={() => setSelectedPackage(pack)}
                        />
                    ))}
                </div>
    
                <PurchaseButton 
                    creditPackage={selectedPackage} 
                    onClose={onClose} 
                    onPurchase={onPurchase}
                    onClick={() => toggleOrderSummary()}
                />
                
            </BaseModal>
            
            <CheckoutModal
                isOpen={isOrderSummaryOpen}
                onClose={() => setIsOrderSummaryOpen(false)}
                selectedPackage={selectedPackage}
            />
        </>
    );
};

export const CreditPackske = ({data, isSelected, onSelect}: any) => {
    const {name, totalCredits, totalPrice} = data as CreditPackageData;
    const priceRounded = Math.floor((totalPrice / totalCredits) * 100) / 100;
    
    return (
        <div
            onClick={onSelect}
            className={`pricing-plan group relative bg-surface-950 rounded-xl p-6 text-center 
            cursor-pointer transition-all duration-200 hover:scale-103 hover:shadow-xl border-2 
            ${isSelected ? 'border-brand-600' : 'border-surface-800'} `}
        >

            {data.isPopular && (
                <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-950 
                    text-brand-400 font-bold text-[10px] px-2 py-1 rounded-md uppercase">
                    Most Popular
                </div>
            )}

            <Typography as="h3" variant="h3">{name}</Typography>
            <Typography as="p" className="text-sm">${totalPrice}</Typography>
            <Typography as="p" className="text-xs">${priceRounded} / icon</Typography>
            
        </div>
    );
};

export const PurchaseButton = ({creditPackage, onClose, onPurchase}: any) => {
    const handlePurchase = () => {
        onPurchase(creditPackage);
        onClose();
    };
    
    return (
        <div className="w-1/2 ml-auto">
            <Button 
                intent="primary" 
                size="large" 
                hover={true}
                glow={true}
                onClick={handlePurchase}
            >
                <LucideRocket className="w-5 h-5 mr-3"/>
                Buy Credits
            </Button>
        </div>
    );
};


























